import type {
  ComplianceBenchmarkPosture,
  ComplianceComment,
  ComplianceExceptionInput,
  ComplianceFilterOptions,
  ComplianceFinding,
  ComplianceFindingDetail,
  ComplianceOccurrence,
  CompliancePage,
  ComplianceScanStats,
  ComplianceStats,
  ComplianceTimelineEvent,
} from '../../app/modules/types/compliance'
import { apiClient } from '../client'

/**
 * The eleven compliance endpoints, all relative to `/api/v1/` and reached
 * through APISIX with cookies (the shared `apiClient` already carries
 * `withCredentials` and the `X-Current-Organization` header).
 *
 * There is deliberately no create, update or delete for a finding itself:
 * findings are created only by a scan, and **the verdict is read-only** — no
 * endpoint changes `status`, because letting a human mark a failed control
 * "passed" would destroy the one number the feature exists to produce. The
 * user's action is an exception.
 */
export const complianceApi = {
  getFindings: async (params?: string): Promise<CompliancePage<ComplianceFinding>> => {
    const url = params ? `/compliance/findings/?${params}` : '/compliance/findings/'
    const { data } = await apiClient.get(url)
    return data
  },

  /** With `asset`, the options are limited to values present on that asset. */
  getFilterOptions: async (asset?: string): Promise<ComplianceFilterOptions> => {
    const url = asset
      ? `/compliance/findings/filter/?asset=${encodeURIComponent(asset)}`
      : '/compliance/findings/filter/'
    const { data } = await apiClient.get(url)
    return data
  },

  getFinding: async (findingId: string): Promise<ComplianceFindingDetail> => {
    const { data } = await apiClient.get(`/compliance/findings/${findingId}/`)
    return data
  },

  /** Per-scan history of this control. */
  getOccurrences: async (
    findingId: string,
    params?: string,
  ): Promise<CompliancePage<ComplianceOccurrence>> => {
    const url = params
      ? `/compliance/findings/${findingId}/occurrences/?${params}`
      : `/compliance/findings/${findingId}/occurrences/`
    const { data } = await apiClient.get(url)
    return data
  },

  /** Verdict changes, exceptions and comments, newest first. */
  getTimeline: async (
    findingId: string,
    params?: string,
  ): Promise<CompliancePage<ComplianceTimelineEvent>> => {
    const url = params
      ? `/compliance/findings/${findingId}/timeline/?${params}`
      : `/compliance/findings/${findingId}/timeline/`
    const { data } = await apiClient.get(url)
    return data
  },

  getComments: async (
    findingId: string,
    params?: string,
  ): Promise<CompliancePage<ComplianceComment>> => {
    const url = params
      ? `/compliance/findings/${findingId}/comments/?${params}`
      : `/compliance/findings/${findingId}/comments/`
    const { data } = await apiClient.get(url)
    return data
  },

  addComment: async (findingId: string, body: string): Promise<ComplianceComment> => {
    const { data } = await apiClient.post(`/compliance/findings/${findingId}/comments/`, { body })
    return data
  },

  /** Only the author may edit; anyone else gets a 403 on authorship. */
  editComment: async (
    findingId: string,
    commentId: string,
    body: string,
  ): Promise<ComplianceComment> => {
    const { data } = await apiClient.patch(
      `/compliance/findings/${findingId}/comments/${commentId}/`,
      { body },
    )
    return data
  },

  /** Soft-delete, author only. */
  deleteComment: async (findingId: string, commentId: string): Promise<void> => {
    await apiClient.delete(`/compliance/findings/${findingId}/comments/${commentId}/`)
  },

  /**
   * Record an exception. The response is the updated finding — note its
   * `status` is unchanged: an exception is accepted risk, not a pass.
   */
  setException: async (
    findingId: string,
    payload: ComplianceExceptionInput,
  ): Promise<ComplianceFindingDetail> => {
    const { data } = await apiClient.put(`/compliance/findings/${findingId}/exception/`, payload)
    return data
  },

  clearException: async (findingId: string): Promise<void> => {
    await apiClient.delete(`/compliance/findings/${findingId}/exception/`)
  },

  getStats: async (): Promise<ComplianceStats> => {
    const { data } = await apiClient.get('/compliance/stats/')
    return data
  },

  getBenchmarks: async (): Promise<ComplianceBenchmarkPosture[]> => {
    const { data } = await apiClient.get('/compliance/benchmarks/')
    return data
  },

  getScanStats: async (scanId: string): Promise<ComplianceScanStats> => {
    const { data } = await apiClient.get(`/compliance/scans/${scanId}/stats/`)
    return data
  },
}
